import { c as _c } from "react/compiler-runtime";
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tab, Tabs } from '@repo/design-system/components/ds/tabs/tabs';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Checkbox } from '@repo/design-system/components/ui/checkbox';
import { InfoBanner } from '@repo/design-system/components/ui/info-banner';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Select, SelectItem } from '@repo/design-system/components/ui/select';
import { Tag } from '@repo/design-system/components/ui/tag';
import { TextArea } from '@repo/design-system/components/ui/text-area';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import type { MapDataHeroAction, MapDataHeroActionIconCategory } from '@repo/types/maps/map-types';
import { MarkdownEditor } from '@repo/ui/components/markdown-editor/markdown-editor';
import type { HeroActionGeneration } from '@repo/wemod-api-client/maps-api-client';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { getHeroActionIconGlyph, HERO_ACTION_ICON_OPTIONS, NEW_HERO_ACTION_EXTERNAL_ID } from './constants';
import { OrderedPinEditor } from './ordered-pin-editor';
import { colMetaStyles, colPinsStyles, externalIdChipStyles, externalIdValueStyles, footLeftStyles, generationPanelStyles, generationStatusBannerStyles, headActionsStyles, headEyebrowDotStyles, headEyebrowStyles, headStackStyles, headTagStripStyles, headTitleStyles, headTitleTextStyles, heroActionEditorModalClass, heroActionEditorShellStyles, modalBodyStyles, modalFootStyles, modalHeadStyles, pinSectionHeadStyles, pinTabPanelStyles, tabHintStyles, titleIconStyles } from './styles';
import type { HeroActionEditorPinDetails, HeroActionEditorPinOption } from './types';
import { getGenerationButtonLabel, getGenerationStatusText, parsePinExternalIds } from './utils';
export const HeroActionEditorModal = t0 => {
  const $ = _c(229);
  const {
    isOpen,
    heroAction,
    name,
    description,
    iconCategory,
    priority,
    resultPins,
    triggerPins,
    generationDescription,
    prompt,
    lockToCurrentMap,
    enableWebSearch,
    keepCurrentPins,
    generationStatus,
    isGenerating,
    isSaving,
    isPreviewing,
    isLoadingPinDetails,
    canPreview,
    hasUnsavedChanges,
    currentMapPinOptions,
    getPinDetails,
    onClose,
    onDiscardChanges,
    onNameChange,
    onDescriptionChange,
    onIconCategoryChange,
    onPriorityChange,
    onResultPinsChange,
    onTriggerPinsChange,
    onGenerationDescriptionChange,
    onPromptChange,
    onLockToCurrentMapChange,
    onEnableWebSearchChange,
    onKeepCurrentPinsChange,
    onGenerate,
    onCancelGeneration,
    onPreview,
    onSave
  } = t0;
  const [activeTab, setActiveTab] = useState("results");
  const pinPanelId = useId();
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  let t1;
  if ($[0] !== resultPins) {
    t1 = parsePinExternalIds(resultPins);
    $[0] = resultPins;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resultPinCount = t1.length;
  let t2;
  if ($[2] !== triggerPins) {
    t2 = parsePinExternalIds(triggerPins);
    $[2] = triggerPins;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const triggerPinCount = t2.length;
  let t3;
  if ($[4] !== resultPins || $[5] !== triggerPins) {
    t3 = new Set([...parsePinExternalIds(resultPins), ...parsePinExternalIds(triggerPins)]);
    $[4] = resultPins;
    $[5] = triggerPins;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const heroActionPinIds = t3;
  let t4;
  if ($[7] !== isGenerating || $[8] !== isSaving || $[9] !== name || $[10] !== resultPinCount || $[11] !== triggerPinCount) {
    t4 = !isSaving && !isGenerating && name.trim() !== "" && resultPinCount > 0 && triggerPinCount > 0;
    $[7] = isGenerating;
    $[8] = isSaving;
    $[9] = name;
    $[10] = resultPinCount;
    $[11] = triggerPinCount;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const canSave = t4;
  const statusLabel = heroAction.isApproved ? "Live" : "Draft";
  const isNewHeroAction = heroAction.externalId === NEW_HERO_ACTION_EXTERNAL_ID;
  const generationActionLabel = isNewHeroAction ? "Generate" : "Regenerate Pins";
  let t5;
  if ($[13] !== iconCategory) {
    t5 = HERO_ACTION_ICON_OPTIONS.find(option => option.value === iconCategory)?.label ?? "Hero Action";
    $[13] = iconCategory;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const iconLabel = t5;
  let t6;
  if ($[15] !== iconCategory) {
    t6 = getHeroActionIconGlyph(iconCategory);
    $[15] = iconCategory;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const glyph = t6;
  const generationStatusVariant = generationStatus?.state === "failed" || generationStatus?.state === "timed_out" ? "error" : "info";
  let t7;
  if ($[17] !== hasUnsavedChanges || $[18] !== onClose) {
    t7 = () => {
      if (hasUnsavedChanges) {
        setIsDiscardConfirmOpen(true);
        return;
      }
      onClose();
    };
    $[17] = hasUnsavedChanges;
    $[18] = onClose;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const requestClose = t7;
  let t8;
  if ($[20] !== onClose || $[21] !== onDiscardChanges) {
    t8 = () => {
      onDiscardChanges();
      setIsDiscardConfirmOpen(false);
      onClose();
    };
    $[20] = onClose;
    $[21] = onDiscardChanges;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const discardAndClose = t8;
  let t10;
  let t9;
  if ($[23] !== isOpen) {
    t9 = () => {
      if (!isOpen) {
        setIsDiscardConfirmOpen(false);
      }
    };
    t10 = [isOpen];
    $[23] = isOpen;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] !== requestClose) {
    t11 = details => {
      if (!details.open) {
        requestClose();
      }
    };
    $[26] = requestClose;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== iconLabel) {
    t12 = <span>{iconLabel}</span>;
    $[28] = iconLabel;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className={headEyebrowDotStyles} />;
    t14 = <span>Hero Action</span>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t13 = $[30];
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12) {
    t15 = <div className={headEyebrowStyles}>{t12}{t13}{t14}</div>;
    $[32] = t12;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = css({
      color: "brand.primary"
    });
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== glyph) {
    t17 = <div className={titleIconStyles}><Icon icon={glyph} fill={true} size="md" decorative={true} className={t16} /></div>;
    $[35] = glyph;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = name || heroAction.name || "Untitled hero action";
  let t19;
  if ($[37] !== name || $[38] !== t18) {
    t19 = <Type variant="header-l" color="white" className={headTitleTextStyles} title={name}>{t18}</Type>;
    $[37] = name;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t17 || $[41] !== t19) {
    t20 = <div className={headTitleStyles}>{t17}{t19}</div>;
    $[40] = t17;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = heroAction.isApproved ? "brand" : "ghost";
  let t22;
  if ($[43] !== statusLabel || $[44] !== t21) {
    t22 = <Tag variant={t21}>{statusLabel}</Tag>;
    $[43] = statusLabel;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Icon icon="fingerprint" decorative={true} className={css({
      fontSize: "[12px]",
      color: "whiteAlpha.60"
    })} />;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== heroAction.externalId) {
    t24 = <Tag variant="ghost" className={externalIdChipStyles}>{t23}<span className={externalIdValueStyles}>{heroAction.externalId}</span></Tag>;
    $[47] = heroAction.externalId;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== heroAction.externalId || $[50] !== t24) {
    t25 = <Tooltip content={heroAction.externalId} size="sm" placement="bottom">{t24}</Tooltip>;
    $[49] = heroAction.externalId;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t22 || $[53] !== t25) {
    t26 = <div className={headTagStripStyles}>{t22}{t25}</div>;
    $[52] = t22;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t15 || $[56] !== t20 || $[57] !== t26) {
    t27 = <div className={headStackStyles}>{t15}{t20}{t26}</div>;
    $[55] = t15;
    $[56] = t20;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Icon icon="close" decorative={true} />;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== requestClose) {
    t29 = <div className={headActionsStyles}><Tooltip content="Close editor" size="sm"><IconButton onClick={requestClose} color="secondary" size="sm" aria-label="Close editor">{t28}</IconButton></Tooltip></div>;
    $[60] = requestClose;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t27 || $[63] !== t29) {
    t30 = <header className={modalHeadStyles}>{t27}{t29}</header>;
    $[62] = t27;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== onNameChange) {
    t31 = e => onNameChange(e.target.value);
    $[65] = onNameChange;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== name || $[68] !== t31) {
    t32 = <TextInput label="Name" value={name} onChange={t31} placeholder="e.g. All NPCs" variant="dark" size="sm" fullWidth={true} />;
    $[67] = name;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== description || $[71] !== onDescriptionChange) {
    t33 = <MarkdownEditor label="Description" value={description} onChange={onDescriptionChange} variant="dark" size="sm" fullWidth={true} maxLength={2000} />;
    $[70] = description;
    $[71] = onDescriptionChange;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== onIconCategoryChange) {
    t34 = value => onIconCategoryChange(value as MapDataHeroActionIconCategory);
    $[73] = onIconCategoryChange;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== glyph) {
    t35 = glyph && <Icon icon={glyph} decorative={true} />;
    $[75] = glyph;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== iconLabel || $[78] !== t35) {
    t36 = <Button color="secondary" size="sm" fullWidth={true}>{t35}{iconLabel}</Button>;
    $[77] = iconLabel;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = HERO_ACTION_ICON_OPTIONS.map(_temp);
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== iconCategory || $[82] !== t34 || $[83] !== t36) {
    t38 = <Select label="Icon" value={iconCategory} onValueChange={t34} size="sm" fullWidth={true} aria-label="Select hero action icon category" trigger={t36}>{t37}</Select>;
    $[81] = iconCategory;
    $[82] = t34;
    $[83] = t36;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== onPriorityChange) {
    t39 = e_0 => onPriorityChange(e_0.target.value);
    $[85] = onPriorityChange;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== priority || $[88] !== t39) {
    t40 = <TextInput label="Priority" type="number" value={priority} onChange={t39} placeholder="0" variant="dark" size="sm" fullWidth={true} />;
    $[87] = priority;
    $[88] = t39;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] !== t38 || $[91] !== t40) {
    t41 = <FlexRow gap="12">{t38}{t40}</FlexRow>;
    $[90] = t38;
    $[91] = t40;
    $[92] = t41;
  } else {
    t41 = $[92];
  }
  const t42 = isGenerating || isSaving;
  let t43;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Icon icon="auto_awesome" decorative={true} />;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== generationActionLabel || $[95] !== generationStatus || $[96] !== isGenerating) {
    t44 = isGenerating ? getGenerationButtonLabel(generationStatus) : generationActionLabel;
    $[94] = generationActionLabel;
    $[95] = generationStatus;
    $[96] = isGenerating;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  let t45;
  if ($[98] !== onGenerate || $[99] !== t42 || $[100] !== t44) {
    t45 = <Button onClick={onGenerate} color="primary" size="sm" disabled={t42}>{t43}{t44}</Button>;
    $[98] = onGenerate;
    $[99] = t42;
    $[100] = t44;
    $[101] = t45;
  } else {
    t45 = $[101];
  }
  let t46;
  if ($[102] !== isGenerating || $[103] !== onCancelGeneration) {
    t46 = isGenerating && <Button onClick={onCancelGeneration} color="secondary" size="sm"><Icon icon="close" decorative={true} />Cancel</Button>;
    $[102] = isGenerating;
    $[103] = onCancelGeneration;
    $[104] = t46;
  } else {
    t46 = $[104];
  }
  let t47;
  if ($[105] !== t45 || $[106] !== t46) {
    t47 = <FlexRow gap="8" justify="end">{t45}{t46}</FlexRow>;
    $[105] = t45;
    $[106] = t46;
    $[107] = t47;
  } else {
    t47 = $[107];
  }
  const t48 = isNewHeroAction ? "Description" : "Additional Details";
  let t49;
  if ($[108] !== onGenerationDescriptionChange) {
    t49 = e_1 => onGenerationDescriptionChange(e_1.target.value);
    $[108] = onGenerationDescriptionChange;
    $[109] = t49;
  } else {
    t49 = $[109];
  }
  const t50 = isNewHeroAction ? "What should this hero action find?" : "What should change about the pins?";
  let t51;
  if ($[110] !== generationDescription || $[111] !== t48 || $[112] !== t49 || $[113] !== t50) {
    t51 = <TextArea label={t48} value={generationDescription} onChange={t49} placeholder={t50} variant="dark" size="sm" resize="vertical" fullWidth={true} rows={4} />;
    $[110] = generationDescription;
    $[111] = t48;
    $[112] = t49;
    $[113] = t50;
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] !== onPromptChange) {
    t52 = e_2 => onPromptChange(e_2.target.value);
    $[115] = onPromptChange;
    $[116] = t52;
  } else {
    t52 = $[116];
  }
  let t53;
  if ($[117] !== prompt || $[118] !== t52) {
    t53 = <TextArea label="Prompt" value={prompt} onChange={t52} placeholder="Hero action generation prompt" variant="dark" size="sm" resize="vertical" fullWidth={true} rows={5} />;
    $[117] = prompt;
    $[118] = t52;
    $[119] = t53;
  } else {
    t53 = $[119];
  }
  let t54;
  if ($[120] !== isNewHeroAction || $[121] !== lockToCurrentMap || $[122] !== onLockToCurrentMapChange) {
    t54 = isNewHeroAction && <Checkbox id="hero-action-editor-current-map-only" label="Current map only" variant="dark" size="sm" checked={lockToCurrentMap} onCheckedChange={details_0 => onLockToCurrentMapChange(details_0.checked === true)} />;
    $[120] = isNewHeroAction;
    $[121] = lockToCurrentMap;
    $[122] = onLockToCurrentMapChange;
    $[123] = t54;
  } else {
    t54 = $[123];
  }
  let t55;
  if ($[124] !== onEnableWebSearchChange) {
    t55 = details_1 => onEnableWebSearchChange(details_1.checked === true);
    $[124] = onEnableWebSearchChange;
    $[125] = t55;
  } else {
    t55 = $[125];
  }
  let t56;
  if ($[126] !== enableWebSearch || $[127] !== t55) {
    t56 = <Checkbox id="hero-action-editor-enable-web-search" label="Web search" variant="dark" size="sm" checked={enableWebSearch} onCheckedChange={t55} />;
    $[126] = enableWebSearch;
    $[127] = t55;
    $[128] = t56;
  } else {
    t56 = $[128];
  }
  let t57;
  if ($[129] !== isNewHeroAction || $[130] !== keepCurrentPins || $[131] !== onKeepCurrentPinsChange) {
    t57 = !isNewHeroAction && <Checkbox id="hero-action-editor-keep-current-pins" label="Keep current pins" variant="dark" size="sm" checked={keepCurrentPins} onCheckedChange={details_2 => onKeepCurrentPinsChange(details_2.checked === true)} />;
    $[129] = isNewHeroAction;
    $[130] = keepCurrentPins;
    $[131] = onKeepCurrentPinsChange;
    $[132] = t57;
  } else {
    t57 = $[132];
  }
  let t58;
  if ($[133] !== t54 || $[134] !== t56 || $[135] !== t57) {
    t58 = <FlexRow gap="12" wrap="wrap" align="center">{t54}{t56}{t57}</FlexRow>;
    $[133] = t54;
    $[134] = t56;
    $[135] = t57;
    $[136] = t58;
  } else {
    t58 = $[136];
  }
  let t59;
  if ($[137] !== generationStatus || $[138] !== generationStatusVariant) {
    t59 = generationStatus && <InfoBanner variant={generationStatusVariant} borderRadius="xs" className={generationStatusBannerStyles}>{getGenerationStatusText(generationStatus)}</InfoBanner>;
    $[137] = generationStatus;
    $[138] = generationStatusVariant;
    $[139] = t59;
  } else {
    t59 = $[139];
  }
  let t60;
  if ($[140] !== t47 || $[141] !== t51 || $[142] !== t53 || $[143] !== t58 || $[144] !== t59) {
    t60 = <FlexColumn gap="10" className={generationPanelStyles}>{t47}{t51}{t53}{t58}{t59}</FlexColumn>;
    $[140] = t47;
    $[141] = t51;
    $[142] = t53;
    $[143] = t58;
    $[144] = t59;
    $[145] = t60;
  } else {
    t60 = $[145];
  }
  let t61;
  if ($[146] !== t32 || $[147] !== t33 || $[148] !== t41 || $[149] !== t60) {
    t61 = <aside className={colMetaStyles}><FlexColumn gap="16" width="full" align="stretch">{t32}{t33}{t41}{t60}</FlexColumn></aside>;
    $[146] = t32;
    $[147] = t33;
    $[148] = t41;
    $[149] = t60;
    $[150] = t61;
  } else {
    t61 = $[150];
  }
  let t62;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = value_0 => setActiveTab(value_0 === "triggers" ? "triggers" : "results");
    $[151] = t62;
  } else {
    t62 = $[151];
  }
  let t63;
  if ($[152] !== resultPinCount) {
    t63 = <Tab value="results" icon="flag">Result Pins ({resultPinCount})</Tab>;
    $[152] = resultPinCount;
    $[153] = t63;
  } else {
    t63 = $[153];
  }
  let t64;
  if ($[154] !== triggerPinCount) {
    t64 = <Tab value="triggers" icon="bolt">Trigger Pins ({triggerPinCount})</Tab>;
    $[154] = triggerPinCount;
    $[155] = t64;
  } else {
    t64 = $[155];
  }
  let t65;
  if ($[156] !== activeTab || $[157] !== pinPanelId || $[158] !== t63 || $[159] !== t64) {
    t65 = <Tabs value={activeTab} onValueChange={t62} controls={pinPanelId} aria-label="Select hero action pin type">{t63}{t64}</Tabs>;
    $[156] = activeTab;
    $[157] = pinPanelId;
    $[158] = t63;
    $[159] = t64;
    $[160] = t65;
  } else {
    t65 = $[160];
  }
  let t66;
  if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <Icon icon="info" decorative={true} className={css({
      fontSize: "[12px]",
      color: "whiteAlpha.40"
    })} />;
    $[161] = t66;
  } else {
    t66 = $[161];
  }
  const t67 = activeTab === "results" ? "Shown to players when this hero action runs" : "Pins that, when clicked, fire this hero action";
  let t68;
  if ($[162] !== t67) {
    t68 = <div className={tabHintStyles}>{t66}{t67}</div>;
    $[162] = t67;
    $[163] = t68;
  } else {
    t68 = $[163];
  }
  let t69;
  if ($[164] !== t65 || $[165] !== t68) {
    t69 = <div className={pinSectionHeadStyles}>{t65}{t68}</div>;
    $[164] = t65;
    $[165] = t68;
    $[166] = t69;
  } else {
    t69 = $[166];
  }
  const t70 = activeTab === "results" ? "Result Pins" : "Trigger Pins";
  let t71;
  if ($[167] !== activeTab || $[168] !== currentMapPinOptions || $[169] !== getPinDetails || $[170] !== heroActionPinIds || $[171] !== onResultPinsChange || $[172] !== onTriggerPinsChange || $[173] !== resultPins || $[174] !== triggerPins) {
    t71 = activeTab === "results" ? <OrderedPinEditor key="results" title="Result Pins" value={resultPins} onChange={onResultPinsChange} getPinDetails={getPinDetails} currentMapPinOptions={currentMapPinOptions} excludedCurrentMapPinIds={heroActionPinIds} /> : <OrderedPinEditor key="triggers" title="Trigger Pins" value={triggerPins} onChange={onTriggerPinsChange} getPinDetails={getPinDetails} currentMapPinOptions={currentMapPinOptions} excludedCurrentMapPinIds={heroActionPinIds} />;
    $[167] = activeTab;
    $[168] = currentMapPinOptions;
    $[169] = getPinDetails;
    $[170] = heroActionPinIds;
    $[171] = onResultPinsChange;
    $[172] = onTriggerPinsChange;
    $[173] = resultPins;
    $[174] = triggerPins;
    $[175] = t71;
  } else {
    t71 = $[175];
  }
  let t72;
  if ($[176] !== pinPanelId || $[177] !== t70 || $[178] !== t71) {
    t72 = <div id={pinPanelId} role="tabpanel" aria-label={t70} className={pinTabPanelStyles}>{t71}</div>;
    $[176] = pinPanelId;
    $[177] = t70;
    $[178] = t71;
    $[179] = t72;
  } else {
    t72 = $[179];
  }
  let t73;
  if ($[180] !== t69 || $[181] !== t72) {
    t73 = <section className={colPinsStyles}>{t69}{t72}</section>;
    $[180] = t69;
    $[181] = t72;
    $[182] = t73;
  } else {
    t73 = $[182];
  }
  let t74;
  if ($[183] !== t61 || $[184] !== t73) {
    t74 = <div className={modalBodyStyles}>{t61}{t73}</div>;
    $[183] = t61;
    $[184] = t73;
    $[185] = t74;
  } else {
    t74 = $[185];
  }
  const t75 = isLoadingPinDetails ? "sync" : "save";
  let t76;
  if ($[186] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = css({
      fontSize: "[12px]",
      color: "whiteAlpha.40"
    });
    $[186] = t76;
  } else {
    t76 = $[186];
  }
  let t77;
  if ($[187] !== t75) {
    t77 = <Icon icon={t75} decorative={true} className={t76} />;
    $[187] = t75;
    $[188] = t77;
  } else {
    t77 = $[188];
  }
  const t78 = isLoadingPinDetails ? "Loading related map pin data\u2026" : `Changes are saved when you select ${isNewHeroAction ? "Save Draft" : "Save Changes"}`;
  let t79;
  if ($[189] !== t77 || $[190] !== t78) {
    t79 = <div className={footLeftStyles}>{t77}{t78}</div>;
    $[189] = t77;
    $[190] = t78;
    $[191] = t79;
  } else {
    t79 = $[191];
  }
  let t80;
  if ($[192] !== requestClose) {
    t80 = <Button variant="ghost" onClick={requestClose} color="secondary" size="md">Cancel</Button>;
    $[192] = requestClose;
    $[193] = t80;
  } else {
    t80 = $[193];
  }
  const t81 = isPreviewing || isGenerating || !canPreview;
  let t82;
  if ($[194] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = <Icon icon="visibility" decorative={true} />;
    $[194] = t82;
  } else {
    t82 = $[194];
  }
  const t83 = isPreviewing ? "Previewing" : "Preview Changes";
  let t84;
  if ($[195] !== onPreview || $[196] !== t81 || $[197] !== t83) {
    t84 = <Button onClick={onPreview} color="secondary" size="md" disabled={t81}>{t82}{t83}</Button>;
    $[195] = onPreview;
    $[196] = t81;
    $[197] = t83;
    $[198] = t84;
  } else {
    t84 = $[198];
  }
  const t85 = !canSave;
  let t86;
  if ($[199] === Symbol.for("react.memo_cache_sentinel")) {
    t86 = <Icon icon="save" decorative={true} />;
    $[199] = t86;
  } else {
    t86 = $[199];
  }
  const t87 = isSaving ? "Saving" : isNewHeroAction ? "Save Draft" : "Save Changes";
  let t88;
  if ($[200] !== onSave || $[201] !== t85 || $[202] !== t87) {
    t88 = <Button onClick={onSave} color="primary" size="md" disabled={t85}>{t86}{t87}</Button>;
    $[200] = onSave;
    $[201] = t85;
    $[202] = t87;
    $[203] = t88;
  } else {
    t88 = $[203];
  }
  let t89;
  if ($[204] !== t80 || $[205] !== t84 || $[206] !== t88) {
    t89 = <FlexRow gap="8" align="center">{t80}{t84}{t88}</FlexRow>;
    $[204] = t80;
    $[205] = t84;
    $[206] = t88;
    $[207] = t89;
  } else {
    t89 = $[207];
  }
  let t90;
  if ($[208] !== t79 || $[209] !== t89) {
    t90 = <footer className={modalFootStyles}>{t79}{t89}</footer>;
    $[208] = t79;
    $[209] = t89;
    $[210] = t90;
  } else {
    t90 = $[210];
  }
  let t91;
  if ($[211] !== t30 || $[212] !== t74 || $[213] !== t90) {
    t91 = <div className={heroActionEditorShellStyles}>{t30}{t74}{t90}</div>;
    $[211] = t30;
    $[212] = t74;
    $[213] = t90;
    $[214] = t91;
  } else {
    t91 = $[214];
  }
  let t92;
  if ($[215] !== isOpen || $[216] !== t11 || $[217] !== t91) {
    t92 = <Modal isOpen={isOpen} onOpenChange={t11} aria-label="Hero Action Editor" hideCloseButton={true} disableInternalScrolling={true} size="custom" className={heroActionEditorModalClass}>{t91}</Modal>;
    $[215] = isOpen;
    $[216] = t11;
    $[217] = t91;
    $[218] = t92;
  } else {
    t92 = $[218];
  }
  let t93;
  if ($[219] === Symbol.for("react.memo_cache_sentinel")) {
    t93 = details_3 => {
      if (!details_3.open) {
        setIsDiscardConfirmOpen(false);
      }
    };
    $[219] = t93;
  } else {
    t93 = $[219];
  }
  let t94;
  if ($[220] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = <Button onClick={() => setIsDiscardConfirmOpen(false)} color="secondary" size="md">Keep Editing</Button>;
    $[220] = t94;
  } else {
    t94 = $[220];
  }
  let t95;
  if ($[221] !== discardAndClose) {
    t95 = <FlexRow gap="8" justify="end" width="full">{t94}<Button onClick={discardAndClose} color="primary" size="md">Discard Changes</Button></FlexRow>;
    $[221] = discardAndClose;
    $[222] = t95;
  } else {
    t95 = $[222];
  }
  let t96;
  if ($[223] !== isDiscardConfirmOpen || $[224] !== t95) {
    t96 = <Modal isOpen={isDiscardConfirmOpen} onOpenChange={t93} title="Discard unsaved changes?" description="Your hero action edits will reset to the saved version." size="sm" zIndex={70}>{t95}</Modal>;
    $[223] = isDiscardConfirmOpen;
    $[224] = t95;
    $[225] = t96;
  } else {
    t96 = $[225];
  }
  let t97;
  if ($[226] !== t92 || $[227] !== t96) {
    t97 = <>{t92}{t96}</>;
    $[226] = t92;
    $[227] = t96;
    $[228] = t97;
  } else {
    t97 = $[228];
  }
  return t97;
};
function _temp(option_0) {
  return <SelectItem key={option_0.value} item={option_0}>{option_0.label}</SelectItem>;
}